import type { JSX } from 'react'
import type { ReconciliationMonth } from '../api'
import { isNegative, monthLabel, yen } from '../format'

// Plan vs actual payroll. Does the headcount plan for months that already
// happened show up in what freee actually paid? The freee side counts everyone
// freee paid — employee payslips plus contractor payments (the （源泉有）-marked
// partners). Compared as GROWTH from a common anchor month (the "delta vs plan
// hires" basis): the plan's all-people baseline and freee's measured level
// cancel, leaving net-new hire cost on each side. A month BELOW plan is the one
// to notice — a scheduled hire the plan expected is not (yet) reflected in
// payments.
//
// Everything here is aggregate + plan only; a month freee suppresses for
// k-anonymity arrives as 'insufficient_data' with the figures blank.

const STATUS: Record<string, { label: string; cls: string; title: string }> = {
  reconciled: { label: 'On plan', cls: 'status-ok', title: 'freee payments are within ¥500k/mo of the plan' },
  under_plan: { label: 'Under plan', cls: 'status-flag', title: 'freee is BELOW plan — a scheduled hire is not reflected in payments' },
  over_plan: { label: 'Over plan', cls: 'status-warn', title: 'freee is running above plan — unplanned hires or raises' },
  insufficient_data: { label: '—', cls: 'status-none', title: 'No anchor yet, or this month is suppressed for k-anonymity' },
}

export function PlanVsActualPayroll({ rows }: { rows: ReconciliationMonth[] }): JSX.Element {
  if (rows.length === 0) {
    return (
      <section className="panel">
        <h2>Plan vs actual payroll</h2>
        <p className="notice">
          Awaiting freee payroll data. Once the freee HR sync is authorized, this reconciles the
          headcount plan against what freee actually paid — flagging months where a planned hire is
          not reflected in payments.
        </p>
      </section>
    )
  }

  const anchor = rows.find((r) => r.anchorMonth !== null)?.anchorMonth ?? null
  const underPlan = rows.filter((r) => r.status === 'under_plan').length

  return (
    <section className="panel">
      <h2>Plan vs actual payroll</h2>
      <p className="panel-note">
        Growth in freee’s monthly payroll run-rate (employee salaries incl. employer welfare +
        contractor payments, bonuses excluded) vs the headcount plan’s net-new hires, both measured
        from{' '}
        {anchor === null ? 'the first freee month' : monthLabel(anchor)}
        {' '}— so the existing team cancels on both sides.{' '}
        {underPlan > 0 ? (
          <strong className="status-flag-text">
            {underPlan} month{underPlan === 1 ? '' : 's'} below plan.
          </strong>
        ) : (
          'No month is below plan.'
        )}
      </p>
      <div className="scroller">
        <table>
          <thead>
            <tr>
              <th scope="col" className="rowhead">
                Month
              </th>
              <th scope="col">freee payroll</th>
              <th scope="col">Actual growth</th>
              <th scope="col">Planned growth</th>
              <th scope="col">Divergence</th>
              <th scope="col">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const status = STATUS[row.status] ?? STATUS.insufficient_data
              const suppressed = row.status === 'insufficient_data'
              return (
                <tr key={row.month} className={row.status === 'under_plan' ? 'flagged' : ''}>
                  <th scope="row" className="rowhead">
                    {monthLabel(row.month)}
                  </th>
                  <td className={suppressed ? 'unknown' : ''}>
                    {suppressed ? 'suppressed' : yen(row.freeeSalaryCost)}
                  </td>
                  <td className={isNegative(row.actualGrowth) ? 'negative' : ''}>{yen(row.actualGrowth)}</td>
                  <td>{yen(row.plannedGrowth)}</td>
                  <td className={isNegative(row.divergence) ? 'negative' : ''}>{yen(row.divergence)}</td>
                  <td>
                    <span className={`status-badge ${status?.cls ?? 'status-none'}`} title={status?.title}>
                      {status?.label}
                    </span>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </section>
  )
}
